import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Megaphone,
  Calendar,
  Gift,
  Copy,
  Mail,
  MessageCircle,
  Search,
  Check,
  Sparkles,
  Send,
  History,
  FileText,
  Clock,
  User,
  ArrowRight,
  Percent,
  CheckCircle2,
  RefreshCw,
} from 'lucide-react';
import { api } from '@/lib/api.js';
import { useToast } from '@/context/ToastContext.js';
import { useAuth } from '@/context/AuthContext.js';
import { Card, CardContent } from '@/components/ui/card.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import {
  HolidayEvent,
  PromotionTemplate,
  PromotionHistoryItem,
  Client,
  PromotionRegion,
} from '@/types/index.js';
import {
  ALL_HOLIDAYS,
  PROMOTION_TEMPLATES,
  calculateCountdown,
  interpolateTemplate,
  buildEmailLink,
  buildWhatsAppLink,
} from '@/lib/promotionData.js';
import { getCountryFlag, cleanPhoneForWhatsApp } from '@/lib/clientUtils.js';

export function PromotionsPage() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState<'holidays' | 'templates' | 'composer' | 'history'>('holidays');

  // Data collections
  const [holidays, setHolidays] = useState<HolidayEvent[]>([]);
  const [templates, setTemplates] = useState<PromotionTemplate[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [history, setHistory] = useState<PromotionHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [regionFilter, setRegionFilter] = useState<PromotionRegion>('all');
  const [quarterFilter, setQuarterFilter] = useState<'all' | 'Q1' | 'Q2' | 'Q3' | 'Q4'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [templateCategoryFilter, setTemplateCategoryFilter] = useState<string>('all');
  const [historyChannelFilter, setHistoryChannelFilter] = useState<string>('all');

  // Composer Form State
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [clientCountryFilter, setClientCountryFilter] = useState<string>('all');
  const [selectedHolidayId, setSelectedHolidayId] = useState<string>('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [customDiscount, setCustomDiscount] = useState<string>('25%');
  const [customDeadline, setCustomDeadline] = useState<string>('');
  const [customSubject, setCustomSubject] = useState<string>('');
  const [customBody, setCustomBody] = useState<string>('');
  const [isSending, setIsSending] = useState(false);
  const [copiedSuccess, setCopiedSuccess] = useState(false);

  // Load initial data
  const loadData = async () => {
    try {
      setLoading(true);
      const [hRes, tRes, cRes, histRes] = await Promise.all([
        api.get('/promotions/holidays'),
        api.get('/promotions/templates'),
        api.get('/clients'),
        api.get('/promotions/history'),
      ]);

      const holidaysList: HolidayEvent[] = hRes?.data || hRes || ALL_HOLIDAYS;
      const templatesList: PromotionTemplate[] = tRes?.data || tRes || PROMOTION_TEMPLATES;
      const clientsList: Client[] = cRes?.data || cRes || [];
      const historyList: PromotionHistoryItem[] = histRes?.data || histRes || [];

      setHolidays(holidaysList);
      setTemplates(templatesList);
      setClients(clientsList);
      setHistory(historyList);

      // Handle query params from navigation
      const paramClientId = searchParams.get('client_id');
      const paramHolidayId = searchParams.get('holiday_id');
      const paramTab = searchParams.get('tab');

      if (paramClientId && clientsList.some((c) => c.id === paramClientId)) {
        setSelectedClientId(paramClientId);
      } else if (clientsList.length > 0 && !selectedClientId) {
        setSelectedClientId(clientsList[0].id);
      }

      if (paramHolidayId) {
        setSelectedHolidayId(paramHolidayId);
      } else if (holidaysList.length > 0 && !selectedHolidayId) {
        setSelectedHolidayId(holidaysList[0].id);
      }

      if (paramTab === 'composer' || paramClientId) {
        setActiveTab('composer');
      } else if (paramTab === 'templates' || paramTab === 'history') {
        setActiveTab(paramTab);
      }
    } catch (err: any) {
      console.error('Failed to load promotions data:', err);
      toast.error('Failed to load holidays or promotions data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Sync template and holiday into composer defaults
  useEffect(() => {
    if (templates.length > 0 && !selectedTemplateId) {
      const defaultTmpl =
        templates.find((t) => t.holiday_id === selectedHolidayId) || templates[0];
      setSelectedTemplateId(defaultTmpl.id);
    }
  }, [templates, selectedHolidayId, selectedTemplateId]);

  // Respond to searchParams changes dynamically
  useEffect(() => {
    const paramClientId = searchParams.get('client_id');
    const paramHolidayId = searchParams.get('holiday_id');
    const paramTab = searchParams.get('tab');

    if (paramClientId && clients.some((c) => c.id === paramClientId)) {
      setSelectedClientId(paramClientId);
    }
    if (paramHolidayId && holidays.some((h) => h.id === paramHolidayId)) {
      setSelectedHolidayId(paramHolidayId);
      const matched = templates.find((t) => t.holiday_id === paramHolidayId);
      if (matched) {
        setSelectedTemplateId(matched.id);
        setCustomSubject(matched.subject);
        setCustomBody(matched.body);
        if (matched.suggested_discount) setCustomDiscount(matched.suggested_discount);
      }
    }
    if (paramTab === 'composer' || paramClientId) {
      setActiveTab('composer');
    } else if (paramTab === 'templates' || paramTab === 'history' || paramTab === 'holidays') {
      setActiveTab(paramTab);
    }
  }, [searchParams, clients, holidays, templates]);

  // When selected holiday changes, update deadline
  useEffect(() => {
    const hol = holidays.find((h) => h.id === selectedHolidayId);
    if (hol && !customDeadline) {
      setCustomDeadline(hol.date);
    }
  }, [selectedHolidayId, holidays]);

  // Selected client record
  const selectedClient = useMemo(() => {
    return clients.find((c) => c.id === selectedClientId) || null;
  }, [clients, selectedClientId]);

  // Selected holiday record
  const selectedHoliday = useMemo(() => {
    return holidays.find((h) => h.id === selectedHolidayId) || null;
  }, [holidays, selectedHolidayId]);

  // Selected template record
  const selectedTemplate = useMemo(() => {
    return templates.find((t) => t.id === selectedTemplateId) || null;
  }, [templates, selectedTemplateId]);

  // Interpolated content
  const interpolated = useMemo(() => {
    const rawSubject = selectedTemplate?.subject || 'Exclusive Holiday Offer for {company_name}';
    const rawBody =
      selectedTemplate?.body ||
      'Hi {client_name},\n\nWe have an exclusive offer for {company_name} with {discount_percent} off before {deadline}.\n\nBest regards,\n{sender_name}';

    return interpolateTemplate(
      { subject: customSubject || rawSubject, body: customBody || rawBody },
      {
        clientName: selectedClient?.name || 'Partner',
        companyName: selectedClient?.company || selectedClient?.name || 'your company',
        discountPercent: customDiscount || '25%',
        deadline: customDeadline || selectedHoliday?.date || 'the upcoming holiday',
        senderName: user?.name || 'Techosolution Team',
      }
    );
  }, [
    selectedTemplate,
    customSubject,
    customBody,
    selectedClient,
    customDiscount,
    customDeadline,
    selectedHoliday,
    user,
  ]);

  // When user picks a new template or holiday, reset custom overrides
  const handleSelectTemplate = (tmplId: string) => {
    setSelectedTemplateId(tmplId);
    const tmpl = templates.find((t) => t.id === tmplId);
    if (tmpl) {
      setCustomSubject(tmpl.subject);
      setCustomBody(tmpl.body);
      if (tmpl.suggested_discount) {
        setCustomDiscount(tmpl.suggested_discount);
      }
      if (tmpl.holiday_id) {
        setSelectedHolidayId(tmpl.holiday_id);
      }
    }
  };

  const handleSelectHolidayForComposer = (holId: string) => {
    setSelectedHolidayId(holId);
    const hol = holidays.find((h) => h.id === holId);
    if (hol) {
      setCustomDeadline(hol.date);
      // Try to find matching template
      const matched = templates.find((t) => t.holiday_id === holId);
      if (matched) {
        handleSelectTemplate(matched.id);
      }
    }
    setActiveTab('composer');
  };

  // Filtered holidays list
  const filteredHolidays = useMemo(() => {
    return holidays.filter((h) => {
      if (regionFilter !== 'all' && h.region !== regionFilter) return false;
      if (quarterFilter !== 'all' && h.quarter !== quarterFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const match =
          h.title.toLowerCase().includes(q) ||
          h.description.toLowerCase().includes(q) ||
          h.suggested_offer.toLowerCase().includes(q) ||
          h.region_label.toLowerCase().includes(q);
        if (!match) return false;
      }
      return true;
    });
  }, [holidays, regionFilter, quarterFilter, searchQuery]);

  // Unique client countries
  const clientCountries = useMemo(() => {
    const list = Array.from(
      new Set(clients.map((c) => c.country).filter(Boolean) as string[])
    );
    return list.sort();
  }, [clients]);

  // Filtered clients in composer
  const composerClients = useMemo(() => {
    if (clientCountryFilter === 'all') return clients;
    return clients.filter((c) => c.country === clientCountryFilter);
  }, [clients, clientCountryFilter]);

  // Filtered templates list
  const filteredTemplates = useMemo(() => {
    return templates.filter((t) => {
      if (
        templateCategoryFilter !== 'all' &&
        t.category.toLowerCase() !== templateCategoryFilter.toLowerCase()
      ) {
        return false;
      }
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (
          t.title.toLowerCase().includes(q) ||
          t.subject.toLowerCase().includes(q) ||
          t.body.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [templates, templateCategoryFilter, searchQuery]);

  // Filtered history
  const filteredHistory = useMemo(() => {
    return history.filter((h) => {
      if (historyChannelFilter !== 'all' && h.channel !== historyChannelFilter) return false;
      return true;
    });
  }, [history, historyChannelFilter]);

  // Quick stats
  const upcomingIn30Days = useMemo(() => {
    return holidays.filter((h) => {
      const cd = calculateCountdown(h.date);
      return cd.daysUntil >= 0 && cd.daysUntil <= 30;
    }).length;
  }, [holidays]);

  // 1-Click Action Dispatchers
  const handleCopyPitch = async () => {
    const textToCopy = `${interpolated.subject}\n\n${interpolated.body}`;
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopiedSuccess(true);
      toast.success('Promotional pitch and subject copied to clipboard!');
      setTimeout(() => setCopiedSuccess(false), 2500);
    } catch {
      toast.error('Failed to copy to clipboard.');
    }
  };

  const handleSendEmail = () => {
    if (!selectedClient?.email) {
      toast.warning('Selected client does not have a primary email address configured.');
      return;
    }
    const link = buildEmailLink(selectedClient.email, interpolated.subject, interpolated.body);
    window.location.href = link;
    toast.info('Prepared email in your default mail client.');
  };

  const handleSendWhatsApp = () => {
    const phone = selectedClient?.mobile || selectedClient?.phone;
    if (!phone) {
      toast.warning('Selected client does not have a mobile or phone number configured.');
      return;
    }
    const digits = cleanPhoneForWhatsApp(phone);
    if (!digits) {
      toast.warning('Client phone format is invalid for international WhatsApp messaging.');
      return;
    }
    const link = buildWhatsAppLink(digits, `${interpolated.subject}\n\n${interpolated.body}`);
    if (link) {
      window.open(link, '_blank');
      toast.success('Opened WhatsApp chat with prefilled personalized pitch.');
    }
  };

  const handleRecordDispatch = async (channel: 'email' | 'whatsapp' | 'copy' | 'manual' = 'email') => {
    if (!selectedClientId) {
      toast.error('Please select a target client first.');
      return;
    }

    try {
      setIsSending(true);
      const payload = {
        client_id: selectedClientId,
        holiday_id: selectedHolidayId || undefined,
        template_id: selectedTemplateId || undefined,
        channel,
        sent_subject: interpolated.subject,
        sent_content: interpolated.body,
        discount_percent: customDiscount,
        deadline: customDeadline,
      };

      const res = await api.post('/promotions/send', payload);
      const createdItem: PromotionHistoryItem = res?.data || res;

      setHistory((prev) => [createdItem, ...prev]);
      toast.success(
        `Campaign dispatched and logged for ${selectedClient?.name || 'client'} via ${channel}!`
      );
    } catch (err: any) {
      console.error('Failed to record campaign dispatch:', err);
      toast.error(err?.message || 'Failed to dispatch and log promotion campaign.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-12">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gradient-to-r from-teal-900/10 via-emerald-800/10 to-teal-900/5 dark:from-teal-950/40 dark:to-emerald-950/30 p-6 rounded-2xl border border-teal-200/50 dark:border-teal-800/40 shadow-xs">
        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <span className="p-2 rounded-xl bg-teal-600 text-white shadow-sm">
              <Megaphone className="w-5 h-5" />
            </span>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
              Promotions & Holiday Marketing Studio
            </h1>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-300 max-w-2xl">
            Whole-year calendar covering USA 🇺🇸, Europe 🇪🇺, Pakistan 🇵🇰, and Global 🌐 tech sales.
            Generate high-converting personalized pitches and dispatch them directly to clients in 1 click.
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadData}
            disabled={loading}
            className="flex items-center space-x-1.5"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </Button>
          <Button
            onClick={() => setActiveTab('composer')}
            className="bg-brand text-white hover:bg-brand-teal flex items-center space-x-2 shadow-sm"
          >
            <Sparkles className="w-4 h-4 text-brand-lime" />
            <span>Compose Pitch</span>
          </Button>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border border-slate-200/80 dark:border-brand-border/70 shadow-xs">
          <CardContent className="p-4 flex items-center space-x-3.5">
            <div className="p-2.5 rounded-xl bg-teal-50 dark:bg-teal-950/50 text-teal-600 dark:text-teal-300">
              <Calendar className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Tracked Holidays</p>
              <p className="text-2xl font-extrabold text-slate-900 dark:text-white font-mono">
                {holidays.length || 34}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200/80 dark:border-brand-border/70 shadow-xs">
          <CardContent className="p-4 flex items-center space-x-3.5">
            <div className="p-2.5 rounded-xl bg-amber-50 dark:bg-amber-950/50 text-amber-600 dark:text-amber-300">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Next 30 Days</p>
              <p className="text-2xl font-extrabold text-amber-600 dark:text-amber-400 font-mono">
                {upcomingIn30Days}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200/80 dark:border-brand-border/70 shadow-xs">
          <CardContent className="p-4 flex items-center space-x-3.5">
            <div className="p-2.5 rounded-xl bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-300">
              <Gift className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Ready Pitch Templates</p>
              <p className="text-2xl font-extrabold text-slate-900 dark:text-white font-mono">
                {templates.length || 10}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-200/80 dark:border-brand-border/70 shadow-xs">
          <CardContent className="p-4 flex items-center space-x-3.5">
            <div className="p-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-300">
              <History className="w-5 h-5" />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 dark:text-slate-400">Dispatched Campaigns</p>
              <p className="text-2xl font-extrabold text-emerald-600 dark:text-emerald-400 font-mono">
                {history.length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Navigation Tabs */}
      <div className="flex items-center space-x-2 border-b border-slate-200 dark:border-brand-border pb-1 overflow-x-auto scrollbar-none">
        <button
          onClick={() => setActiveTab('holidays')}
          className={`flex items-center space-x-2 px-4 py-2.5 rounded-t-lg text-sm font-semibold border-b-2 transition-colors shrink-0 ${
            activeTab === 'holidays'
              ? 'border-brand text-brand bg-teal-50/50 dark:bg-teal-950/30'
              : 'border-transparent text-slate-600 hover:text-slate-900 dark:text-slate-400'
          }`}
        >
          <Calendar className="w-4 h-4" />
          <span>Holidays & Events ({holidays.length})</span>
        </button>

        <button
          onClick={() => setActiveTab('templates')}
          className={`flex items-center space-x-2 px-4 py-2.5 rounded-t-lg text-sm font-semibold border-b-2 transition-colors shrink-0 ${
            activeTab === 'templates'
              ? 'border-brand text-brand bg-teal-50/50 dark:bg-teal-950/30'
              : 'border-transparent text-slate-600 hover:text-slate-900 dark:text-slate-400'
          }`}
        >
          <FileText className="w-4 h-4" />
          <span>Pitch Templates ({templates.length})</span>
        </button>

        <button
          onClick={() => setActiveTab('composer')}
          className={`flex items-center space-x-2 px-4 py-2.5 rounded-t-lg text-sm font-semibold border-b-2 transition-colors shrink-0 ${
            activeTab === 'composer'
              ? 'border-brand text-brand bg-teal-50/50 dark:bg-teal-950/30'
              : 'border-transparent text-slate-600 hover:text-slate-900 dark:text-slate-400'
          }`}
        >
          <Sparkles className="w-4 h-4 text-amber-500" />
          <span>Campaign Composer</span>
        </button>

        <button
          onClick={() => setActiveTab('history')}
          className={`flex items-center space-x-2 px-4 py-2.5 rounded-t-lg text-sm font-semibold border-b-2 transition-colors shrink-0 ${
            activeTab === 'history'
              ? 'border-brand text-brand bg-teal-50/50 dark:bg-teal-950/30'
              : 'border-transparent text-slate-600 hover:text-slate-900 dark:text-slate-400'
          }`}
        >
          <History className="w-4 h-4" />
          <span>Dispatch History ({history.length})</span>
        </button>
      </div>

      {/* =====================================================================
          TAB 1: HOLIDAYS & EVENTS CALENDAR
          ===================================================================== */}
      {activeTab === 'holidays' && (
        <div className="space-y-4">
          {/* Controls Bar */}
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 bg-white dark:bg-[#12211F] p-3.5 rounded-xl border border-slate-200 dark:border-brand-border">
            {/* Regional Filter Pills */}
            <div className="flex items-center space-x-1.5 overflow-x-auto pb-1 lg:pb-0 scrollbar-none">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider px-1">Region:</span>
              {[
                { id: 'all', label: 'All Regions' },
                { id: 'usa', label: 'USA 🇺🇸' },
                { id: 'europe', label: 'Europe 🇪🇺' },
                { id: 'pakistan', label: 'Pakistan 🇵🇰' },
                { id: 'global', label: 'Global 🌐' },
              ].map((r) => (
                <button
                  key={r.id}
                  onClick={() => setRegionFilter(r.id as PromotionRegion)}
                  className={`px-3 py-1 text-xs font-semibold rounded-full border transition-all whitespace-nowrap ${
                    regionFilter === r.id
                      ? 'bg-brand text-white border-brand shadow-xs'
                      : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>

            {/* Quarter & Search Filter */}
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg text-xs font-medium">
                {(['all', 'Q1', 'Q2', 'Q3', 'Q4'] as const).map((q) => (
                  <button
                    key={q}
                    onClick={() => setQuarterFilter(q)}
                    className={`px-2 py-0.5 rounded-md transition-colors ${
                      quarterFilter === q
                        ? 'bg-white dark:bg-slate-700 font-bold text-brand shadow-xs'
                        : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    {q === 'all' ? 'Year' : q}
                  </button>
                ))}
              </div>

              <div className="relative min-w-[200px]">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search holidays..."
                  className="pl-8 h-8 text-xs"
                />
              </div>
            </div>
          </div>

          {/* Holiday Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredHolidays.map((holiday) => {
              const cd = calculateCountdown(holiday.date);
              const isUrgent = cd.daysUntil >= 0 && cd.daysUntil <= 7;
              const isToday = cd.isToday;

              return (
                <Card
                  key={holiday.id}
                  className={`border transition-all duration-fast hover:shadow-md ${
                    isToday
                      ? 'border-red-400 bg-red-50/20 dark:border-red-600 dark:bg-red-950/20 ring-1 ring-red-400'
                      : isUrgent
                      ? 'border-amber-300 dark:border-amber-700/80 bg-amber-50/20 dark:bg-amber-950/20'
                      : 'border-slate-200/90 dark:border-brand-border bg-white dark:bg-[#12211F]'
                  }`}
                >
                  <CardContent className="p-4 flex flex-col justify-between h-full space-y-3">
                    <div>
                      {/* Top Badges */}
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="inline-flex items-center space-x-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200/60 dark:border-slate-700">
                          <span>{holiday.region_label}</span>
                        </span>

                        <span
                          className={`text-xs px-2.5 py-0.5 rounded-full font-bold flex items-center space-x-1 ${
                            isToday
                              ? 'bg-red-500 text-white animate-pulse'
                              : isUrgent
                              ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/80 dark:text-amber-300 border border-amber-300'
                              : cd.daysUntil > 0 && cd.daysUntil <= 30
                              ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/80 dark:text-emerald-300'
                              : cd.isPast
                              ? 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                              : 'bg-teal-50 text-teal-700 dark:bg-teal-950/60 dark:text-teal-300'
                          }`}
                        >
                          <Clock className="w-3 h-3" />
                          <span>{cd.badge}</span>
                        </span>
                      </div>

                      {/* Title & Date */}
                      <h3 className="text-base font-bold text-slate-900 dark:text-white leading-snug">
                        {holiday.title}
                      </h3>
                      <div className="flex items-center space-x-2 text-xs text-slate-500 dark:text-slate-400 mt-1">
                        <Calendar className="w-3.5 h-3.5 text-slate-400" />
                        <span className="font-mono">
                          {new Date(`${holiday.date}T00:00:00Z`).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </span>
                        <span>•</span>
                        <span className="uppercase font-semibold text-[10px] tracking-wider text-slate-400">
                          {holiday.quarter}
                        </span>
                      </div>

                      {/* Description */}
                      <p className="text-xs text-slate-600 dark:text-slate-300 mt-2.5 line-clamp-2">
                        {holiday.description}
                      </p>

                      {/* Suggested Offer Callout */}
                      <div className="mt-3 p-2.5 rounded-lg bg-teal-50/60 dark:bg-teal-950/40 border border-teal-200/50 dark:border-teal-800/40 text-xs">
                        <span className="font-semibold text-teal-800 dark:text-teal-200 flex items-center space-x-1 mb-0.5">
                          <Percent className="w-3 h-3 text-teal-600" />
                          <span>Recommended Campaign:</span>
                        </span>
                        <p className="text-teal-950 dark:text-teal-100 font-medium">
                          {holiday.suggested_offer}
                        </p>
                      </div>
                    </div>

                    {/* Card Action */}
                    <div className="pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                      <span className="text-[11px] font-medium text-slate-400">
                        {holiday.category.toUpperCase()}
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleSelectHolidayForComposer(holiday.id)}
                        className="text-xs font-semibold text-brand hover:text-brand-teal hover:bg-brand-tint/40 flex items-center space-x-1"
                      >
                        <span>Pitch Clients</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* =====================================================================
          TAB 2: PITCH TEMPLATES
          ===================================================================== */}
      {activeTab === 'templates' && (
        <div className="space-y-4">
          {/* Category Filter */}
          <div className="flex items-center space-x-2 bg-white dark:bg-[#12211F] p-3 rounded-xl border border-slate-200 dark:border-brand-border overflow-x-auto scrollbar-none">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider px-1">Category:</span>
            {['all', 'commercial', 'tech', 'cultural'].map((cat) => (
              <button
                key={cat}
                onClick={() => setTemplateCategoryFilter(cat)}
                className={`px-3 py-1 text-xs font-semibold rounded-full border transition-all capitalize whitespace-nowrap ${
                  templateCategoryFilter === cat
                    ? 'bg-brand text-white border-brand'
                    : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-100'
                }`}
              >
                {cat === 'all' ? 'All Templates' : cat}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredTemplates.map((tmpl) => (
              <Card
                key={tmpl.id}
                className="border border-slate-200/90 dark:border-brand-border bg-white dark:bg-[#12211F] hover:shadow-md transition-shadow"
              >
                <CardContent className="p-5 flex flex-col justify-between h-full space-y-4">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="text-xs px-2.5 py-0.5 rounded-full font-bold bg-teal-100 text-teal-800 dark:bg-teal-950 dark:text-teal-300">
                        {tmpl.suggested_discount} Offer
                      </span>
                      <span className="text-[11px] font-mono text-slate-400 uppercase">
                        Channel: {tmpl.recommended_channel || 'All'}
                      </span>
                    </div>

                    <h3 className="text-base font-bold text-slate-900 dark:text-white">
                      {tmpl.title}
                    </h3>

                    {/* Subject line preview */}
                    <div className="mt-2 text-xs font-mono bg-slate-50 dark:bg-slate-800/80 p-2 rounded-md border border-slate-200/80 dark:border-slate-700 text-slate-700 dark:text-slate-300">
                      <span className="font-bold text-slate-400">Subject: </span>
                      {tmpl.subject}
                    </div>

                    {/* Body preview */}
                    <p className="mt-3 text-xs text-slate-600 dark:text-slate-300 whitespace-pre-line line-clamp-5 bg-slate-50/50 dark:bg-slate-900/40 p-3 rounded-lg border border-slate-100 dark:border-slate-800 font-sans">
                      {tmpl.body}
                    </p>

                    {/* Target services */}
                    {tmpl.target_services && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {tmpl.target_services.map((svc) => (
                          <span
                            key={svc}
                            className="text-[10px] px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
                          >
                            {svc}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-end">
                    <Button
                      size="sm"
                      onClick={() => {
                        handleSelectTemplate(tmpl.id);
                        setActiveTab('composer');
                      }}
                      className="bg-brand hover:bg-brand-teal text-white text-xs font-semibold flex items-center space-x-1.5"
                    >
                      <Sparkles className="w-3.5 h-3.5 text-brand-lime" />
                      <span>Use In Composer</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* =====================================================================
          TAB 3: CAMPAIGN COMPOSER & DISPATCHER
          ===================================================================== */}
      {activeTab === 'composer' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* Left Column: Form Controls (7 cols) */}
          <div className="lg:col-span-7 space-y-5">
            {/* Target Client Selection */}
            <Card className="border border-slate-200 dark:border-brand-border bg-white dark:bg-[#12211F] shadow-xs">
              <CardContent className="p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center space-x-2">
                    <User className="w-4 h-4 text-teal-600" />
                    <span>Step 1: Target Client Selection</span>
                  </h3>

                  {/* Filter by country */}
                  {clientCountries.length > 0 && (
                    <select
                      value={clientCountryFilter}
                      onChange={(e) => setClientCountryFilter(e.target.value)}
                      className="text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md px-2 py-1"
                    >
                      <option value="all">All Countries ({clients.length})</option>
                      {clientCountries.map((c) => (
                        <option key={c} value={c}>
                          {getCountryFlag(c)} {c}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                    Select Agency Client
                  </label>
                  <select
                    value={selectedClientId}
                    onChange={(e) => setSelectedClientId(e.target.value)}
                    className="w-full text-sm bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-2.5 font-medium text-slate-900 dark:text-white"
                  >
                    {composerClients.map((c) => (
                      <option key={c.id} value={c.id}>
                        {getCountryFlag(c.country)} {c.name} {c.company ? `(${c.company})` : ''} —{' '}
                        {c.country || 'Global'}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Selected Client Overview Card */}
                {selectedClient && (
                  <div className="p-3.5 rounded-xl bg-teal-50/50 dark:bg-teal-950/30 border border-teal-200/60 dark:border-teal-800/50 flex flex-wrap items-center justify-between gap-3 text-xs">
                    <div className="space-y-0.5">
                      <div className="flex items-center space-x-1.5 font-bold text-slate-900 dark:text-white">
                        <span>{getCountryFlag(selectedClient.country)}</span>
                        <span>{selectedClient.name}</span>
                        {selectedClient.company && (
                          <span className="text-slate-500 font-normal">
                            • {selectedClient.company}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-3 text-slate-600 dark:text-slate-300">
                        <span>Email: {selectedClient.email || '—'}</span>
                        <span>•</span>
                        <span>Mobile: {selectedClient.mobile || selectedClient.phone || '—'}</span>
                      </div>
                    </div>

                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-white dark:bg-slate-800 border border-teal-200 text-teal-800 dark:text-teal-300">
                      {selectedClient.platform || 'direct'} client
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Holiday & Template Selection */}
            <Card className="border border-slate-200 dark:border-brand-border bg-white dark:bg-[#12211F] shadow-xs">
              <CardContent className="p-5 space-y-4">
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center space-x-2">
                  <Gift className="w-4 h-4 text-teal-600" />
                  <span>Step 2: Holiday & Pitch Template</span>
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                      Associated Holiday / Event
                    </label>
                    <select
                      value={selectedHolidayId}
                      onChange={(e) => handleSelectHolidayForComposer(e.target.value)}
                      className="w-full text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-2.5 text-slate-900 dark:text-white font-medium"
                    >
                      {holidays.map((h) => (
                        <option key={h.id} value={h.id}>
                          {h.region_label} {h.title} ({h.date})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                      Pitch Copy Template
                    </label>
                    <select
                      value={selectedTemplateId}
                      onChange={(e) => handleSelectTemplate(e.target.value)}
                      className="w-full text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-2.5 text-slate-900 dark:text-white font-medium"
                    >
                      {templates.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.title} ({t.suggested_discount || '25%'})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Offer Details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-slate-100 dark:border-slate-800">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                      Discount Value
                    </label>
                    <div className="flex items-center space-x-1.5">
                      {['15%', '20%', '25%', '30%'].map((pct) => (
                        <button
                          type="button"
                          key={pct}
                          onClick={() => setCustomDiscount(pct)}
                          className={`px-2.5 py-1 text-xs rounded-md font-semibold border transition-all ${
                            customDiscount === pct
                              ? 'bg-brand text-white border-brand'
                              : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200'
                          }`}
                        >
                          {pct}
                        </button>
                      ))}
                      <Input
                        value={customDiscount}
                        onChange={(e) => setCustomDiscount(e.target.value)}
                        placeholder="Custom %"
                        className="h-7 text-xs w-24"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                      Offer Expiry / Deadline
                    </label>
                    <Input
                      type="date"
                      value={customDeadline}
                      onChange={(e) => setCustomDeadline(e.target.value)}
                      className="h-8 text-xs bg-slate-50 dark:bg-slate-800"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Editable Pitch Subject & Body */}
            <Card className="border border-slate-200 dark:border-brand-border bg-white dark:bg-[#12211F] shadow-xs">
              <CardContent className="p-5 space-y-4">
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 flex items-center space-x-2">
                  <FileText className="w-4 h-4 text-teal-600" />
                  <span>Step 3: Customize Pitch Copy</span>
                </h3>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                    Subject Line (Template)
                  </label>
                  <Input
                    value={customSubject || selectedTemplate?.subject || ''}
                    onChange={(e) => setCustomSubject(e.target.value)}
                    placeholder="e.g. Special Offer for {company_name}"
                    className="text-xs bg-slate-50 dark:bg-slate-800 font-medium"
                  />
                  <p className="text-[10px] text-slate-400">
                    Variables available: {'{client_name}'}, {'{company_name}'},{' '}
                    {'{discount_percent}'}, {'{deadline}'}, {'{sender_name}'}
                  </p>
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                    Message Body (Template)
                  </label>
                  <textarea
                    rows={8}
                    value={customBody || selectedTemplate?.body || ''}
                    onChange={(e) => setCustomBody(e.target.value)}
                    className="w-full text-xs font-sans p-3 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-brand"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column: Live Personalized Preview & 1-Click Action Dispatchers (5 cols) */}
          <div className="lg:col-span-5 space-y-5 sticky top-20">
            <Card className="border-2 border-teal-500/40 dark:border-teal-400/40 bg-white dark:bg-[#12211F] shadow-lg">
              <div className="p-4 border-b border-slate-200 dark:border-brand-border bg-teal-50/40 dark:bg-teal-950/40 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Sparkles className="w-4 h-4 text-brand-lime" />
                  <span className="text-xs font-bold uppercase tracking-wider text-teal-900 dark:text-teal-200">
                    Live Personalized Preview
                  </span>
                </div>
                <Badge variant="mint" className="text-[10px]">
                  Ready to Dispatch
                </Badge>
              </div>

              <CardContent className="p-5 space-y-4">
                {/* Live Subject Preview */}
                <div className="space-y-1">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                    Interpolated Subject
                  </span>
                  <div className="p-2.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-xs font-bold text-slate-900 dark:text-white font-mono break-words border border-slate-200 dark:border-slate-700">
                    {interpolated.subject}
                  </div>
                </div>

                {/* Live Message Body Preview */}
                <div className="space-y-1">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                    Interpolated Message Body
                  </span>
                  <div className="p-3.5 rounded-lg bg-slate-50 dark:bg-slate-900/60 text-xs text-slate-800 dark:text-slate-200 whitespace-pre-line border border-slate-200 dark:border-slate-700 font-sans max-h-72 overflow-y-auto leading-relaxed">
                    {interpolated.body}
                  </div>
                </div>

                {/* 1-Click Action Dispatchers */}
                <div className="pt-3 border-t border-slate-200 dark:border-slate-800 space-y-2.5">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                    1-Click Dispatch Actions
                  </span>

                  <div className="grid grid-cols-2 gap-2">
                    {/* Copy to Clipboard */}
                    <Button
                      type="button"
                      onClick={handleCopyPitch}
                      variant="outline"
                      className="w-full text-xs font-semibold flex items-center justify-center space-x-1.5 h-10 border-slate-300 dark:border-slate-700 hover:bg-slate-100"
                    >
                      {copiedSuccess ? (
                        <>
                          <Check className="w-4 h-4 text-emerald-600" />
                          <span className="text-emerald-700">Copied!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                          <span>Copy Pitch</span>
                        </>
                      )}
                    </Button>

                    {/* Email mailto: link */}
                    <Button
                      type="button"
                      onClick={handleSendEmail}
                      variant="outline"
                      className="w-full text-xs font-semibold flex items-center justify-center space-x-1.5 h-10 border-blue-200 dark:border-blue-900 bg-blue-50/40 dark:bg-blue-950/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100/50"
                    >
                      <Mail className="w-4 h-4 text-blue-600" />
                      <span>Send Email</span>
                    </Button>

                    {/* WhatsApp link */}
                    <Button
                      type="button"
                      onClick={handleSendWhatsApp}
                      variant="outline"
                      className="w-full text-xs font-semibold flex items-center justify-center space-x-1.5 h-10 border-emerald-200 dark:border-emerald-900 bg-emerald-50/40 dark:bg-emerald-950/20 text-[#0f8c51] dark:text-[#22c55e] hover:bg-emerald-100/50"
                    >
                      <MessageCircle className="w-4 h-4 text-emerald-600" />
                      <span>WhatsApp</span>
                    </Button>

                    {/* Quick copy plain text */}
                    <Button
                      type="button"
                      onClick={() => handleRecordDispatch('copy')}
                      variant="outline"
                      className="w-full text-xs font-semibold flex items-center justify-center space-x-1.5 h-10 text-slate-700 dark:text-slate-300"
                    >
                      <CheckCircle2 className="w-4 h-4 text-teal-600" />
                      <span>Log as Copied</span>
                    </Button>
                  </div>

                  {/* Primary Dispatch & Log button */}
                  <Button
                    type="button"
                    onClick={() => handleRecordDispatch('email')}
                    disabled={isSending}
                    className="w-full bg-brand text-white hover:bg-brand-teal flex items-center justify-center space-x-2 h-11 font-bold shadow-md text-sm mt-2"
                  >
                    <Send className="w-4 h-4" />
                    <span>
                      {isSending
                        ? 'Logging Outreach...'
                        : `Mark Dispatched & Log Campaign`}
                    </span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* =====================================================================
          TAB 4: DISPATCH HISTORY
          ===================================================================== */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between bg-white dark:bg-[#12211F] p-3 rounded-xl border border-slate-200 dark:border-brand-border">
            <div className="flex items-center space-x-2">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider px-1">
                Channel Filter:
              </span>
              {['all', 'email', 'whatsapp', 'copy'].map((ch) => (
                <button
                  key={ch}
                  onClick={() => setHistoryChannelFilter(ch)}
                  className={`px-3 py-1 text-xs font-semibold rounded-full border transition-all capitalize ${
                    historyChannelFilter === ch
                      ? 'bg-brand text-white border-brand'
                      : 'bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {ch === 'all' ? 'All Channels' : ch}
                </button>
              ))}
            </div>

            <span className="text-xs text-slate-500 font-mono">
              Total Logged: {filteredHistory.length}
            </span>
          </div>

          <Card className="border border-slate-200 dark:border-brand-border bg-white dark:bg-[#12211F]">
            <CardContent className="p-0 overflow-x-auto">
              {filteredHistory.length === 0 ? (
                <div className="p-12 text-center text-slate-400 text-sm">
                  No promotional campaigns recorded yet. Use the Composer to dispatch a pitch to your clients!
                </div>
              ) : (
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-brand-border bg-slate-50 dark:bg-slate-800/60 font-semibold text-slate-600 dark:text-slate-300">
                      <th className="p-3.5">Dispatched At</th>
                      <th className="p-3.5">Target Client</th>
                      <th className="p-3.5">Holiday / Event</th>
                      <th className="p-3.5">Pitch Template</th>
                      <th className="p-3.5">Channel</th>
                      <th className="p-3.5">Offer</th>
                      <th className="p-3.5">Sender</th>
                      <th className="p-3.5 text-right">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-medium">
                    {filteredHistory.map((item) => (
                      <tr key={item.id} className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40">
                        <td className="p-3.5 font-mono text-slate-500 dark:text-slate-400 whitespace-nowrap">
                          {new Date(item.sent_at).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td className="p-3.5">
                          <div className="font-bold text-slate-900 dark:text-white">
                            {item.client_name}
                          </div>
                          {item.client_company && (
                            <div className="text-[11px] text-slate-400">
                              {item.client_company}
                            </div>
                          )}
                        </td>
                        <td className="p-3.5 text-slate-700 dark:text-slate-200">
                          {item.holiday_title}
                        </td>
                        <td className="p-3.5 text-slate-600 dark:text-slate-300">
                          {item.template_title}
                        </td>
                        <td className="p-3.5">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase inline-flex items-center space-x-1 ${
                              item.channel === 'whatsapp'
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                                : item.channel === 'email'
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300'
                                : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
                            }`}
                          >
                            <span>{item.channel}</span>
                          </span>
                        </td>
                        <td className="p-3.5 font-mono font-bold text-teal-600 dark:text-teal-400">
                          {item.discount_percent || '—'}
                        </td>
                        <td className="p-3.5 text-slate-500 dark:text-slate-400">
                          {item.sent_by_name}
                        </td>
                        <td className="p-3.5 text-right">
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-teal-50 text-teal-700 dark:bg-teal-950/60 dark:text-teal-300 border border-teal-200/60">
                            Delivered
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
export default PromotionsPage;
